import React from 'react';
import styled, { keyframes, withTheme } from 'styled-components';

/**
 * Baseline template radio now-playing UI.
 * Uses the same theme tokens as Common.styled (MenuModalItem, GlassCard, Header).
 */

/**
 * Tizen-safe inline SVG radio icon (stroke-based, no channel image / font dependency).
 * Same handling approach as Warwick templates; colors come from the Baseline theme.
 */
const RadioIconInner = ({ width = 24, height = 24, variant = 'primary', theme, ...props }) => {
  const colors = theme?.colors || {};
  const stroke = {
    primary: colors.accent || colors.buttonPrimary || colors.textPrimary || '#ffffff',
    secondary: colors.textPrimary || '#ffffff',
    outline: colors.textPrimary || '#ffffff',
  }[variant] || colors.textPrimary || '#ffffff';

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width={width}
      height={height}
      viewBox='0 0 24 24'
      fill='none'
      stroke={stroke}
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
      {...props}
    >
      <path d='M4.9 19.1C1 15.2 1 8.8 4.9 4.9M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5' />
      <circle cx='12' cy='12' r='2' stroke={stroke} />
      <path d='M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5M19.1 4.9c3.9 3.9 3.9 10.3 0 14.2' />
    </svg>
  );
};

export const RadioIcon = withTheme(RadioIconInner);

export const RadioPlaybackLayer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const RadioNowPlayingStage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px;
  max-width: 640px;
`;

export const RadioStationCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 48px;
  border-radius: ${(props) => props.theme.effects?.borderRadiusLarge || '16px'};
  background: ${(props) =>
    props.theme.colors?.buttonPrimary ||
    props.theme.colors?.glassBackground ||
    'rgba(0, 0, 0, 0.45)'};
  color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
  min-width: 320px;
`;

export const RadioStationIconBox = styled.div`
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${(props) => props.theme.effects?.borderRadiusMedium || '12px'};
  background: ${(props) => props.theme.colors?.glassBackground || 'rgba(255, 255, 255, 0.12)'};
  margin-bottom: 20px;
  font-size: 52px;
  color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
`;

export const RadioLiveBadge = styled.span`
  display: inline-block;
  padding: 6px 16px;
  margin-bottom: 16px;
  border-radius: ${(props) => props.theme.effects?.borderRadiusMedium || '12px'};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  background: ${(props) =>
    props.$active
      ? props.theme.colors?.accent || props.theme.colors?.buttonPrimary || '#c0392b'
      : 'rgba(255, 255, 255, 0.2)'};
  color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
  opacity: ${(props) => (props.$active ? 1 : 0.6)};
`;

export const RadioStationTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 28px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
  word-wrap: break-word;
`;

export const RadioStationFrequency = styled.p`
  margin: 0;
  font-size: 18px;
  font-weight: 400;
  opacity: 0.85;
  color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
`;

const barPulse = keyframes`
  0%, 100% { transform: scaleY(0.35); opacity: 0.45; }
  50% { transform: scaleY(1); opacity: 1; }
`;

export const RadioSignalBars = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 40px;
  margin-top: 24px;

  span {
    display: block;
    width: 4px;
    margin: 0 2px;
    height: 100%;
    border-radius: 2px;
    background: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
    transform-origin: bottom center;
    animation: ${barPulse} 1.2s ease-in-out infinite;
  }
`;

/** Off-screen mount for device players in radio mode (audio-only streams). */
export const BaselineHiddenPlayerMount = styled.div.attrs(() => ({
  'data-tizen-video-area': 'true',
}))`
  position: fixed;
  left: 0;
  top: 0;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  z-index: -1;
`;
